import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import ReplayIcon from '@mui/icons-material/Replay';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { api, itemImageUrl } from '../api/client';
import type { ErrorText } from '../api/errorText';
import type { CatalogueEntry, LibraryItem, MediaSourceSummary } from '../api/types';
import { useApi } from '../api/useApi';
import AsyncSection from '../components/AsyncSection';
import { usePageTitle } from '../components/PageTitle';
import CastRow from '../components/media/CastRow';
import CatalogueDialog from '../components/media/CatalogueDialog';
import EpisodeList from '../components/media/EpisodeList';
import ItemVersions from '../components/media/ItemVersions';
import MediaGrid from '../components/media/MediaGrid';
import { embedUrl, episodeCode, formatDuration, formatRemaining, toSeconds } from '../components/media/playbackTime';
import { useDominantColour } from '../components/media/useDominantColour';
import { ResumeProgress } from '../components/media/WatchProgress';
import PlayerOverlay from '../components/player/PlayerOverlay';
import VersionPicker from '../components/media/VersionPicker';
import { useAuth } from '../auth/AuthContext';
import { barOffset, groundRgb, shellStart } from '../theme';

/// One film, series, season or episode, given the room a title deserves: its own artwork behind
/// it and the page taking its colour from it. This is also the one screen that says which
/// library something came from - everywhere else that is the server's business.
export default function ItemDetailPage() {
  const { itemId = '' } = useParams();
  const { t } = useTranslation();
  const { can } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Both washes over the artwork fade into the ground the page stands on, so which ground
  // that is decides them: a still is darkened into a dark page and lightened into a light one.
  const dark = theme.palette.mode === 'dark';
  const ground = dark ? groundRgb.dark : groundRgb.light;

  // The scrim runs from the edge the title stands on, which is the right one on an Arabic
  // screen: a wash on the far side would darken the picture and leave the letters on the bare
  // still.
  const toTitle = theme.direction === 'rtl' ? 270 : 90;

  const item = useApi(['items', itemId], () => api.item(itemId));
  const children = useApi(['items', itemId, 'children'], () => api.itemChildren(itemId));
  const libraries = useApi(['libraries'], api.libraries, { enabled: can('ManageLibraries') });

  const [error, setError] = useState<ErrorText>();
  const [trailerOpen, setTrailerOpen] = useState(false);

  /// Set while the player is up: what is playing and from where. It is the whole screen, so
  /// the page under it is untouched.
  const [playing, setPlaying] = useState<{ item: LibraryItem; from: number; mediaSourceId?: string }>();

  /// Which copy of this to play. Undefined until somebody chooses, and then it is theirs for
  /// as long as they are on the page.
  const [version, setVersion] = useState<string>();

  /// Where this person stopped last time, and what the bar under the buttons draws.
  const progress = useApi(['playback', 'progress'], api.playbackProgress);

  /// The files this entry stands for. Asked for only where there is something to play, because
  /// a series has no file of its own.
  const versions = useApi(['items', itemId, 'versions'], () => api.itemVersions(itemId));

  // What is already known to stop the version play would start: said before anybody presses it.
  const knownFailure = (versions.data?.find((candidate) => candidate.id === version) ?? versions.data?.[0])
    ?.knownFailure;
  const started = progress.data?.[itemId];

  /// Which providers can take a version back or fetch another. Only asked by somebody who may
  /// use discovery at all; for everybody else the list of versions stays read-only.
  const discoveryProviders = useApi(['discovery', 'providers'], api.discoveryProviders, {
    enabled: can('Discover'),
  });

  /// Set while the version search for this title is open.
  const [fetchingAnother, setFetchingAnother] = useState<CatalogueEntry>();


  const shown = item.data;
  // Every episode of the series, whichever level of it is open. A series shows its seasons but
  // plays an episode, and which episode that is cannot be answered from one season alone.
  const seriesId = shown?.kind === 'Series' ? shown.id : shown?.seriesId ?? undefined;

  const episodes = useApi(
    ['items', 'episodes', seriesId ?? ''],
    () => api.items({ kind: 'Episode', seriesId, sort: 'Episode', take: 500 }),
    { enabled: Boolean(seriesId) },
  );

  /// Where to carry on in a series: the one that was started and not finished, otherwise the
  /// first that has not been seen, otherwise the very first. The same order a person would go
  /// looking in.
  const nextEpisode = (() => {
    const all = episodes.data?.items ?? [];

    if (all.length === 0) {
      return undefined;
    }

    const started = all.find((episode) => {
      const seen = progress.data?.[episode.id];

      return seen && !seen.isWatched && seen.fraction > 0;
    });

    return started ?? all.find((episode) => !progress.data?.[episode.id]?.isWatched) ?? all[0];
  })();

  const backdrop = shown ? itemImageUrl(shown, 'Backdrop') : undefined;
  const poster = shown ? itemImageUrl(shown) : undefined;

  // The title as it was drawn for this title, where a provider had it. It stands exactly where
  // the written title would have stood - but only over a still. A title treatment is drawn to
  // be laid over artwork, and light letters on the bare page would be a title nobody can read.
  const logo = shown && backdrop ? itemImageUrl(shown, 'Logo') : undefined;

  // The bar above the page has no entry for one film, so the film names itself up there.
  usePageTitle(shown?.title);

  // The poster decides the colour of the page, not the backdrop. A poster is what somebody
  // designed to stand for the title - Pioneer One's is red - while a backdrop is a frame out
  // of it and is usually dark and nearly grey.
  const tint = useDominantColour(poster ?? backdrop);

  /// How far into an episode this person got, which is where pressing play on it starts.
  const watchedUpTo = (episode: LibraryItem) =>
    toSeconds(progress.data?.[episode.id]?.position ?? '00:00:00');

  const play = (target: LibraryItem | undefined, fromSeconds: number) => {
    if (!target?.mediaSourceId) {
      return;
    }

    setError(undefined);

    // The chosen copy belongs to the item on this page; the next episode is a different item
    // and brings its own.
    setPlaying({
      item: target,
      from: fromSeconds,
      mediaSourceId: target.id === shown?.id ? version : undefined,
    });
  };

  const removableBy = new Set((discoveryProviders.data ?? []).map((provider) => provider.providerId));
  const fetchedVersions = (versions.data ?? []).filter(
    (source) => source.reference != null && removableBy.has(source.reference.providerId),
  );

  // Another version is looked for by the film's name, which is what a search can answer. An
  // episode's name is not: the indexer knows the programme, not the fortieth episode of it.
  const canFetchAnother = shown?.kind === 'Movie'
    && fetchedVersions.length > 0
    && (discoveryProviders.data ?? []).some((provider) => provider.isReady);

  /// Takes one version off the shelf. Where it was the last one, the title goes with it and
  /// there is no page left to stand on.
  const removeVersion = async (source: MediaSourceSummary) => {
    if (!source.reference) {
      return;
    }

    await api.removeRelease(source.reference.providerId, source.reference.key);

    if (version === source.id) {
      setVersion(undefined);
    }

    // Everything that shows the shelf asks again - this page's versions among them, and the
    // catalogue tile that said the title is held.
    void queryClient.invalidateQueries({ queryKey: ['items'] });
    void queryClient.invalidateQueries({ queryKey: ['discovery'] });

    if ((versions.data ?? []).length <= 1) {
      void navigate(-1);
    }
  };

  const libraryName = libraries.data?.find((library) => library.id === shown?.libraryId)?.name;
  const resumeSeconds = started ? toSeconds(started.position) : 0;

  const nextEpisodeProgress = nextEpisode ? progress.data?.[nextEpisode.id] : undefined;
  const nextEpisodeSeconds = nextEpisodeProgress ? toSeconds(nextEpisodeProgress.position) : 0;

  /// What the button says. A film continues or plays; a series says which episode it is about
  /// to open, because pressing play on a series should never be a surprise.
  const playLabel = (() => {
    if (shown?.isPlayable) {
      return resumeSeconds > 0 ? t('item.resume') : t('item.play');
    }

    if (!nextEpisode) {
      return t('item.play');
    }

    const number = episodeCode(nextEpisode.seasonNumber ?? 0, nextEpisode.episodeNumber ?? 0);

    return nextEpisodeSeconds > 0
      ? t('item.resumeEpisode', { episode: number })
      : t('item.playEpisode', { episode: number });
  })();

  return (
    <AsyncSection loading={item.loading} error={item.error} onRetry={item.reload}>
      {shown && (
        <Box
          sx={{
            // Out to the edges of the window - on under the glass of the sidebar and up under
            // the bar: the artwork is the page, not a picture on it.
            marginInlineStart: { xs: -2, md: `calc(-32px - ${shellStart})` },
            marginInlineEnd: { xs: -2, md: -4 },
            mt: { xs: `calc(-16px - ${barOffset.xs}px)`, md: `calc(-24px - ${barOffset.md}px)` },

            // The colour of the artwork, held over the whole page and fading out as it goes.
            background: tint
              ? `linear-gradient(180deg, ${tint} 0%, transparent 70%)`
              : undefined,
          }}
        >
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 260 + barOffset.xs, md: 480 + barOffset.md },
              display: 'flex',
              alignItems: 'flex-end',
              paddingInlineStart: { xs: 2, md: `calc(48px + ${shellStart})` },
              paddingInlineEnd: { xs: 2, md: 6 },
              pt: { xs: `${48 + barOffset.xs}px`, md: `${80 + barOffset.md}px` },
              pb: { xs: 3, md: 5 },
              backgroundImage: backdrop ? `url(${backdrop})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center 20%',

              // Two washes over the artwork: one so the page below it does not start with an
              // edge, one so the title stays readable whatever the still happens to be.
              '&::after': {
                content: '""',
                position: 'absolute',
                inset: 0,
                background: backdrop
                  ? `linear-gradient(0deg, rgba(${ground},0.96) 0%, rgba(${ground},0.55) 45%,`
                    + ` rgba(${ground},0.15) 100%),`
                    + ` linear-gradient(${toTitle}deg, rgba(${ground},0.85) 0%, rgba(${ground},0.1) 70%)`
                  : 'none',
                pointerEvents: 'none',
              },
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={{ xs: 2, md: 4 }}
              sx={{ position: 'relative', zIndex: 1, alignItems: 'flex-end', width: '100%' }}
            >
              {poster && (
                <Box
                  component="img"
                  src={poster}
                  alt=""
                  sx={{
                    width: { xs: 110, md: 200 },
                    borderRadius: 2,
                    boxShadow: 10,
                    display: 'block',
                    flexShrink: 0,
                  }}
                />
              )}

              <Stack spacing={1.5} sx={{ minWidth: 0, flexGrow: 1 }}>
                {logo ? (
                  <Box
                    component="img"
                    src={logo}
                    // The picture is the title, so it is read out as the title and not
                    // announced as a picture on top of it.
                    alt={shown.title}
                    sx={{
                      alignSelf: 'flex-start',
                      maxWidth: { xs: 220, sm: 300, md: 420 },
                      maxHeight: { xs: 72, md: 132 },
                      objectFit: 'contain',
                      objectPosition: theme.direction === 'rtl' ? 'right bottom' : 'left bottom',
                      display: 'block',
                      // A title treatment is drawn to sit on a dark still and is usually light,
                      // which a light page would swallow. The halo is the same answer the
                      // written title gets, in the shape a picture takes it.
                      filter: dark
                        ? 'drop-shadow(0 2px 24px rgba(0,0,0,0.6))'
                        : 'drop-shadow(0 1px 2px rgba(0,0,0,0.55)) drop-shadow(0 2px 18px rgba(0,0,0,0.35))',
                    }}
                  />
                ) : (
                  <Typography
                    variant="h1"
                    // The title carries its own ground with it, in case the still behind it is
                    // busy exactly where the letters are.
                    sx={{
                      textShadow: backdrop
                        ? `0 2px 24px rgba(${dark ? '0,0,0' : '255,255,255'},${dark ? 0.6 : 0.85})`
                        : undefined,
                    }}
                  >
                    {shown.title}
                  </Typography>
                )}

                {shown.originalTitle && shown.originalTitle !== shown.title && (
                  <Typography color="text.secondary">{shown.originalTitle}</Typography>
                )}

                {shown.tagline && (
                  <Typography sx={{ fontStyle: 'italic' }} color="text.secondary">
                    {shown.tagline}
                  </Typography>
                )}

                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }} useFlexGap>
                  <Chip size="small" label={t(`itemKind.${shown.kind}`)} />
                  {shown.year && <Chip size="small" variant="outlined" label={shown.year} />}
                  {shown.seasonNumber !== null && shown.episodeNumber !== null && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={episodeCode(shown.seasonNumber, shown.episodeNumber)}
                    />
                  )}
                  {shown.duration && (
                    <Chip size="small" variant="outlined" label={formatDuration(shown.duration)} />
                  )}
                  {shown.officialRating && (
                    // The age rating is a statement by whoever issued it, so it is shown as
                    // they worded it and nothing is inferred from it.
                    <Chip
                      size="small"
                      variant="outlined"
                      label={shown.officialRating}
                      sx={{ fontWeight: 700, borderWidth: 2 }}
                    />
                  )}
                  {shown.communityRating !== null && (
                    <Chip size="small" variant="outlined" label={`★ ${shown.communityRating.toFixed(1)}`} />
                  )}
                  {shown.genres.map((genre) => (
                    <Chip key={genre} size="small" label={genre} />
                  ))}
                </Stack>

                <Stack spacing={1.5} sx={{ pt: 1, maxWidth: 560 }}>
                  <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', alignItems: 'center' }} useFlexGap>
                    {/* A series is not playable itself; what it plays is the episode somebody
                        is up to. */}
                    {(shown.isPlayable || nextEpisode) && (
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<PlayArrowIcon sx={{ fontSize: '2rem !important' }} />}
                        onClick={() =>
                          shown.isPlayable
                            ? play(shown, resumeSeconds)
                            : play(nextEpisode, nextEpisodeSeconds)
                        }
                        sx={{
                          // The one thing on the page somebody came to press.
                          px: 4,
                          py: 1.5,
                          fontSize: '1.05rem',
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        {playLabel}
                      </Button>
                    )}

                    {shown.isPlayable && (
                      <VersionPicker
                        versions={versions.data ?? []}
                        value={version}
                        onChange={setVersion}
                      />
                    )}

                    {shown.isPlayable && resumeSeconds > 0 && (
                      <Button
                        variant="text"
                        size="large"
                        startIcon={<ReplayIcon />}
                        onClick={() => {
                          void api.clearProgress(itemId).then(() => progress.reload());
                          play(shown, 0);
                        }}
                      >
                        {t('item.playFromStart')}
                      </Button>
                    )}

                    {shown.trailerUrl && (
                      <Button
                        variant="outlined"
                        size="large"
                        startIcon={<MovieFilterIcon />}
                        onClick={() => setTrailerOpen((open) => !open)}
                      >
                        {t('item.trailer')}
                      </Button>
                    )}
                  </Stack>

                  {started && started.fraction > 0 && (
                    <Stack spacing={0.5}>
                      <ResumeProgress fraction={started.fraction} />

                      <Typography variant="caption" color="text.secondary">
                        {t('item.remaining', { time: formatRemaining(started.position, started.duration) })}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Stack>
          </Box>

          {/* The picture ends and the page begins; without this they touch and the seam
              shows. */}
          <Stack
            spacing={4}
            sx={{
              paddingInlineStart: { xs: 2, md: `calc(48px + ${shellStart})` },
              paddingInlineEnd: { xs: 2, md: 6 },
              pt: { xs: 3, md: 5 },
              pb: 8,
            }}
          >
            {shown.overview && (
              // A measure, so a description stays readable on a wide screen.
              <Typography sx={{ maxWidth: '70ch' }}>{shown.overview}</Typography>
            )}

            {error && (
              <Alert severity="warning">
                <AlertTitle>{t('item.cannotPlay')}</AlertTitle>
                <div>{error.message}</div>
                {error.remedy && <div>{error.remedy}</div>}
              </Alert>
            )}

            {/* The particulars stay in the server's English: which file, how much of it, what
                fixes it. The headline is what has to be understood at a glance. */}
            {!error && knownFailure && (
              <Alert severity="warning">
                <AlertTitle>{t('item.versionUnplayable')}</AlertTitle>
                <div>{knownFailure.message}</div>
                <div>{knownFailure.remedy}</div>
              </Alert>
            )}

            {trailerOpen && shown.trailerUrl && (
              <Stack spacing={1} sx={{ maxWidth: 960 }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    aspectRatio: '16 / 9',
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'black',
                  }}
                >
                  <Box
                    component="iframe"
                    src={embedUrl(shown.trailerUrl)}
                    title={t('item.trailer')}
                    allow="accelerometer; autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                    sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0 }}
                  />
                </Box>

                {/* Said plainly: this is the one thing on the page that is not served by this
                    server, and it only loads because somebody pressed the button. */}
                <Typography variant="caption" color="text.secondary">
                  {t('item.trailerSource')}
                </Typography>
              </Stack>
            )}

            {shown.people.length > 0 && <CastRow people={shown.people} />}

            {shown.isPlayable && (versions.data?.length ?? 0) > 0 && (
              <ItemVersions
                versions={versions.data ?? []}
                removableBy={removableBy}
                canRemove={can('ManageLibraries')}
                canFetchAnother={canFetchAnother}
                onRemove={removeVersion}
                onFetchAnother={() =>
                  setFetchingAnother({
                    title: {
                      id: shown.id,
                      provider: 'library',
                      kind: 'Movie',
                      title: shown.title,
                      year: shown.year ?? undefined,
                    },
                    libraryItemId: shown.id,
                    canFetch: true,
                  })
                }
              />
            )}

            {(children.data?.items.length ?? 0) > 0 && (
              <Stack spacing={2}>
                <Typography variant="h3">
                  {shown.kind === 'Series' ? t('item.seasons') : t('item.episodes')}
                </Typography>

                {shown.kind === 'Series' ? (
                  <MediaGrid items={children.data?.items ?? []} emptyKey="item.noChildren" />
                ) : (
                  <EpisodeList
                    episodes={children.data?.items ?? []}
                    progress={progress.data}
                    onPlay={(episode) => play(episode, watchedUpTo(episode))}
                  />
                )}
              </Stack>
            )}

            {/* A series shows its seasons above and, under them, the episodes of the season
                somebody is up to - so the next thing to watch is never more than one click
                away. */}
            {shown.kind === 'Series' && (episodes.data?.items.length ?? 0) > 0 && (
              <Stack spacing={2}>
                <Typography variant="h3">{t('item.episodes')}</Typography>

                <EpisodeList
                  episodes={(episodes.data?.items ?? []).filter(
                    (episode) => episode.seasonNumber === (nextEpisode?.seasonNumber ?? null),
                  )}
                  progress={progress.data}
                  onPlay={(episode) => play(episode, watchedUpTo(episode))}
                />
              </Stack>
            )}

            {libraryName && (
              <Typography variant="caption" color="text.secondary">
                {t('item.fromLibrary', { library: libraryName })}
              </Typography>
            )}
          </Stack>

          <CatalogueDialog
            entry={fetchingAnother}
            lookOnOpen
            onClose={() => setFetchingAnother(undefined)}
            onFetched={() => {
              versions.reload();
              item.reload();
            }}
          />

          {playing && (
            <PlayerOverlay
              target={{
                kind: 'item',
                item: playing.item,
                startAt: playing.from,
                mediaSourceId: playing.mediaSourceId,
              }}
              onClose={(saved) => {
                setPlaying(undefined);

                // Where they got to decides what the button says next time - asked for once the
                // player's last word on it has arrived, or it is the position from before.
                void saved.then(() => progress.reload());
              }}
            />
          )}
        </Box>
      )}
    </AsyncSection>
  );
}
